<template>
	<component
		:is="tag"
		:to="url"
		:type="type"
		:class="classes"
		:disabled="disabled || loading"
		@click="$emit('click')"
		@mouseenter="$emit('mouseenter')"
		@mouseleave="$emit('mouseleave')"
		class="btn"
	>
		<Loader size="small" v-if="loading"/>
		<slot/>
	</component>
</template>

<script setup lang="ts">
import Loader from '@shared/components/base/Loader.vue'
import { computed } from 'vue'

interface Props {
	to?: string | object
	size?: string
	color?: string
	type?: string
	wide?: boolean
	disabled?: boolean
	loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
	to: '',
	size: 'middle',
	color: '',
	type: undefined,
	wide: false,
	disabled: false,
	loading: false
})

defineEmits<{
	click: []
	mouseenter: []
	mouseleave: []
}>()

const classes = computed(() => {
	return {
		['btn_size_' + props.size]: props.size,
		btn_wide: props.wide,
		['btn_color_' + props.color]: props.color,
		disabled: props.disabled
	}
})

const tag = computed(() => props.to ? 'router-link' : 'button')
const url = computed(() => props.to ? props.to : null)
</script>

<style>
	.btn {display:flex;align-items:center;justify-content:center;position:relative;font-weight:500;text-decoration:none;}
	.btn svg {pointer-events:none;transition:fill .2s;fill:currentColor;}
	.btn.disabled {opacity:.7;pointer-events:none;}
	.btn .loader {display:flex;align-items:center;justify-content:center;position:absolute;top:0;right:0;bottom:0;left:0;margin:0;background:inherit;border-radius:inherit;}
	.btn .loader__dots p {background:currentColor;}

	/* Size */
	.btn_size_tiny {padding:0 7px;height:24px;font-size:.85rem;border-radius:var(--radius-s);}
	.btn_size_small {padding:2px 8px;min-height:30px;font-size:.95rem;border-radius:var(--radius-s);}
	.btn_size_middle {padding:0 15px;height:40px;font-size:1.05rem;border-radius:var(--radius-l);}
	.btn_size_big {padding:0 25px;height:50px;font-size:1.15rem;border-radius:var(--radius-xl);}
	.btn_size_vbig {padding:0 35px;height:60px;font-size:1.4rem;border-radius:var(--radius-xl);}

	/* Effects */
	.btn_wide {width:100%;}

	/* Color */
	.btn_color_white {color:var(--light5);background:#fff;}
	.btn_color_white:hover {background:var(--light1);}

	.btn_color_ghost {color:var(--light5);border:1px solid #d4d4dc;background:#f3f3f4;box-shadow:0 2px 5px rgba(0,0,0,.1);}
	.btn_color_ghost:hover {background:#e7e7ec;}

	.btn_color_light {color:var(--dark1);background:var(--light2);}
	.btn_color_light:hover {background:#c4c4ce;}

	.btn_color_dark {color:#fff;background:var(--light3);}
	.btn_color_dark:hover {background:var(--light4);}

	.btn_color_yellow {color:#944000;background:#ffb940;}
	.btn_color_yellow:hover {background:#ffa710;}

	.btn_color_violet {color:#fff;background:var(--violet);}
	.btn_color_violet:hover {background:var(--violet2);}

	.btn_color_purple {color:#fff;background:var(--purple2);}
	.btn_color_purple:hover {background:var(--purple1);}

	.btn_color_green {color:#fff;background:var(--green);}
	.btn_color_green:hover {background:var(--green2);}

	/* Media queries */
	@media (max-width:650px) {
		.btn_size_middle {padding:0 10px;height:36px;font-size:1.01rem;}
		.btn_size_big {padding:0 20px;height:46px;font-size:16px;}
	}
</style>

